import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Wallet, Plus, Send, ArrowDownLeft, QrCode, Link2, Copy, RefreshCw,
  X, Check, Search, ChevronRight, ArrowUpRight, ArrowDownRight,
  Clock, AlertCircle, Zap, Shield, Smartphone, CreditCard, Coins,
  Banknote, User, Building2, History, Flag, ExternalLink, Eye, EyeOff,
  Sparkles, Star, Share2, Download, ScanLine
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (ts) => ts ? new Date(ts).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

const METHOD_INFO = {
  stripe:  { icon: CreditCard, label: "Tarjeta de crédito/débito", color: "text-blue-400",   bg: "bg-blue-500/10",   border: "border-blue-500/20" },
  spei:    { icon: Banknote,   label: "SPEI / Transferencia bancaria", color: "text-green-400", bg: "bg-green-500/10", border: "border-green-500/20" },
  crypto:  { icon: Coins,      label: "Criptomonedas (BTC, ETH, USDT)", color: "text-yellow-400", bg: "bg-yellow-500/10", border: "border-yellow-500/20" },
  cash:    { icon: Banknote,   label: "Efectivo (depósito en sucursal)", color: "text-gray-300",  bg: "bg-white/5",      border: "border-white/10" },
};

const TX_TYPE_COLOR = {
  transfer: { out: "text-red-400",    in: "text-green-400" },
  recharge: { in: "text-green-400" },
  refund:   { in: "text-purple-400" },
};

// ─── QR Code Generator (simple canvas-based) ──────────────────────────────────
function QRDisplay({ data, size = 200, label, subtitle }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !data) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = size;
    canvas.height = size;

    // Simple visual QR-like pattern (decorative — real QR uses a library)
    const cellSize = size / 25;
    // background
    ctx.fillStyle = "#0f1117";
    ctx.fillRect(0, 0, size, size);

    const hash = data.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
    const rng = (seed) => {
      let x = Math.sin(seed + hash) * 10000;
      return x - Math.floor(x);
    };

    // Draw QR-like matrix
    for (let row = 0; row < 25; row++) {
      for (let col = 0; col < 25; col++) {
        const isCorner = (row < 7 && col < 7) || (row < 7 && col > 17) || (row > 17 && col < 7);
        const isCornerInner = (row >= 1 && row <= 5 && col >= 1 && col <= 5) ||
                              (row >= 1 && row <= 5 && col >= 19 && col <= 23) ||
                              (row >= 19 && row <= 23 && col >= 1 && col <= 5);
        const isBorder = row === 0 || row === 24 || col === 0 || col === 24;

        if (isCorner) {
          ctx.fillStyle = "#ef4444";
          ctx.fillRect(col * cellSize, row * cellSize, cellSize, cellSize);
          if (isCornerInner) {
            ctx.fillStyle = "#0f1117";
            ctx.fillRect(col * cellSize + 1, row * cellSize + 1, cellSize - 2, cellSize - 2);
          }
        } else if (rng(row * 25 + col) > 0.45) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(col * cellSize + 0.5, row * cellSize + 0.5, cellSize - 1, cellSize - 1);
        }
      }
    }
    // Center logo area
    const logoSize = cellSize * 5;
    const logoX = (size - logoSize) / 2;
    const logoY = (size - logoSize) / 2;
    ctx.fillStyle = "#1a1d2e";
    ctx.beginPath();
    ctx.roundRect(logoX - 4, logoY - 4, logoSize + 8, logoSize + 8, 8);
    ctx.fill();
    ctx.fillStyle = "#ef4444";
    ctx.beginPath();
    ctx.roundRect(logoX, logoY, logoSize, logoSize, 6);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = `bold ${cellSize * 2}px sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("OK", size / 2, size / 2);
  }, [data, size]);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative">
        <div className="absolute -inset-3 rounded-3xl bg-gradient-to-br from-red-500/20 via-purple-500/10 to-blue-500/20 blur-xl" />
        <div className="relative p-4 rounded-2xl" style={{ background: "linear-gradient(135deg, #1a1d2e 0%, #0f1117 100%)", border: "1px solid rgba(239,68,68,0.3)" }}>
          <canvas ref={canvasRef} style={{ borderRadius: 8, display: "block" }} />
        </div>
        {/* Corner decorations */}
        <div className="absolute -top-2 -left-2 w-4 h-4 border-l-2 border-t-2 border-red-400 rounded-tl-lg" />
        <div className="absolute -top-2 -right-2 w-4 h-4 border-r-2 border-t-2 border-red-400 rounded-tr-lg" />
        <div className="absolute -bottom-2 -left-2 w-4 h-4 border-l-2 border-b-2 border-red-400 rounded-bl-lg" />
        <div className="absolute -bottom-2 -right-2 w-4 h-4 border-r-2 border-b-2 border-red-400 rounded-br-lg" />
      </div>
      {label && <p className="text-white font-semibold text-sm mt-1">{label}</p>}
      {subtitle && <p className="text-gray-500 text-xs">{subtitle}</p>}
    </div>
  );
}

// ─── QR Modal (innovador) ─────────────────────────────────────────────────────
function QRModal({ wallet, onClose }) {
  const [tab, setTab] = useState("receive");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [payLink, setPayLink] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyWalletId = () => {
    navigator.clipboard.writeText(wallet.wallet_id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const generatePayLink = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Ingresa un monto válido"); return; }
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/wallet/payment-link`, {
        creator_id: wallet.owner_id,
        amount: parseFloat(amount),
        description: note || `Pago a ${wallet.username}`,
        expires_hours: 24,
      });
      setPayLink(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setGenerating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.92)" }}>
      <div className="relative w-full max-w-sm" style={{ background: "linear-gradient(135deg, #13141f 0%, #0d0e18 100%)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 24 }}>
        {/* Gradient top glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(239,68,68,0.15) 0%, transparent 70%)" }} />

        {/* Header */}
        <div className="relative flex items-center justify-between p-6 pb-4">
          <div>
            <h3 className="text-white font-bold text-lg">Mi OkWallet QR</h3>
            <p className="text-gray-500 text-xs mt-0.5">Escanea para enviar o pagar</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 flex gap-2 mb-5">
          {[
            { id: "receive", label: "Recibir" },
            { id: "paylink", label: "Link de Pago" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${tab === t.id ? "text-white" : "text-gray-500 hover:text-gray-300"}`}
              style={tab === t.id ? { background: "linear-gradient(135deg, rgba(239,68,68,0.25) 0%, rgba(139,92,246,0.15) 100%)", border: "1px solid rgba(239,68,68,0.3)" } : { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "receive" && (
          <div className="px-6 pb-6 flex flex-col items-center gap-4">
            <QRDisplay data={`okwallet://pay?to=${wallet.wallet_id}&user=${wallet.username}`} size={220} />

            {/* Wallet ID */}
            <div className="w-full rounded-2xl p-4" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <p className="text-gray-500 text-xs mb-1.5 font-medium">OkWallet ID</p>
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-red-400 text-sm font-bold tracking-wider">{wallet.wallet_id}</span>
                <button onClick={copyWalletId} className={`p-1.5 rounded-lg transition-all ${copied ? "bg-green-500/20 text-green-400" : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"}`}>
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                </button>
              </div>
            </div>

            {/* Send ways */}
            <div className="w-full">
              <p className="text-gray-500 text-xs font-medium mb-2">Recibe por:</p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: ScanLine, label: "QR Code" },
                  { icon: User, label: `@${wallet.username}` },
                  { icon: Smartphone, label: "WhatsApp" },
                  { icon: Zap, label: "OkWallet ID" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <item.icon size={13} className="text-red-400 flex-shrink-0" />
                    <span className="text-gray-300 text-xs truncate">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === "paylink" && (
          <div className="px-6 pb-6 space-y-4">
            {!payLink ? (
              <>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Monto a cobrar ($MXN) *</label>
                  <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00"
                    className="w-full text-white text-xl font-bold outline-none bg-transparent border-b border-white/20 pb-2 focus:border-red-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1.5 font-medium">Descripción (opcional)</label>
                  <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Ej: Corte de cabello"
                    className="input-glass w-full text-sm" />
                </div>
                <p className="text-gray-600 text-xs">El link expira en 24 horas.</p>
                <button onClick={generatePayLink} disabled={generating}
                  className="w-full py-3 rounded-2xl text-white font-semibold transition-all flex items-center justify-center gap-2"
                  style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" }}>
                  {generating ? <RefreshCw size={16} className="animate-spin" /> : <Link2 size={16} />}
                  Generar Link de Pago
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-green-500/20">
                  <Check size={24} className="text-green-400" />
                </div>
                <div className="text-center">
                  <p className="text-white font-bold text-2xl">${fmt(payLink.amount)}</p>
                  <p className="text-gray-400 text-sm mt-0.5">{payLink.description}</p>
                </div>
                <QRDisplay data={payLink.qr_data} size={200} />
                <div className="w-full rounded-2xl p-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <p className="text-gray-500 text-xs mb-1">Link de pago</p>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-300 text-xs font-mono flex-1 truncate">{payLink.qr_data}</span>
                    <button onClick={() => { navigator.clipboard.writeText(payLink.qr_data); toast.success("Link copiado"); }}
                      className="p-1.5 bg-white/5 rounded-lg text-gray-400 hover:text-white">
                      <Copy size={13} />
                    </button>
                  </div>
                </div>
                <button onClick={() => { setPayLink(null); setAmount(""); setNote(""); }} className="text-gray-500 text-xs hover:text-gray-300">
                  Generar otro
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Transfer Modal ────────────────────────────────────────────────────────────
function TransferModal({ wallet, onClose, onDone }) {
  const [step, setStep] = useState("recipient");
  const [identifier, setIdentifier] = useState("");
  const [resolving, setResolving] = useState(false);
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const resolve = async () => {
    if (!identifier.trim()) return;
    setResolving(true);
    try {
      const r = await axios.get(`${API}/wallet/resolve/${encodeURIComponent(identifier.trim())}`);
      if (r.data.found) {
        setRecipient(r.data);
        setStep("amount");
      } else {
        toast.error("No se encontró una wallet con ese identificador");
      }
    } catch { toast.error("Error al buscar destinatario"); }
    finally { setResolving(false); }
  };

  const send = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Ingresa un monto válido"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/wallet/transfer`, {
        sender_id: wallet.owner_id,
        identifier: identifier.trim(),
        amount: parseFloat(amount),
        description: description || "Transferencia OkWallet",
      });
      setResult(r.data);
      setStep("done");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
    finally { setSending(false); }
  };

  const recipientName = recipient?.user?.name || recipient?.business?.name || recipient?.wallet?.username || identifier;
  const recipientType = recipient?.business ? "business" : "user";

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-sm">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <Send size={16} className="text-red-400" />Enviar Pago
          </h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"><X size={18} /></button>
        </div>

        {step === "recipient" && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-gray-400 text-xs mb-2 font-medium">Buscar destinatario</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input value={identifier} onChange={e => setIdentifier(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && resolve()}
                  placeholder="@nickname, teléfono, OKW..."
                  className="input-glass w-full pl-8" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: User, label: "@nickname" },
                { icon: Smartphone, label: "Teléfono/WhatsApp" },
                { icon: ScanLine, label: "QR Code" },
                { icon: Zap, label: "OkWallet ID" },
              ].map((opt, i) => (
                <div key={i} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/3 border border-white/5">
                  <opt.icon size={13} className="text-gray-500" />
                  <span className="text-gray-500 text-xs">{opt.label}</span>
                </div>
              ))}
            </div>
            <button onClick={resolve} disabled={resolving || !identifier.trim()} className="btn-primary w-full flex items-center justify-center gap-2">
              {resolving ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
              Buscar
            </button>
          </div>
        )}

        {step === "amount" && recipient && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${recipientType === "business" ? "bg-purple-500/20" : "bg-blue-500/20"}`}>
                {recipientType === "business" ? <Building2 size={18} className="text-purple-400" /> : <User size={18} className="text-blue-400" />}
              </div>
              <div>
                <p className="text-white font-medium text-sm">{recipientName}</p>
                <p className="text-gray-500 text-xs font-mono">{recipient.wallet?.wallet_id}</p>
              </div>
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-2">Monto ($MXN)</label>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                placeholder="0.00" className="input-glass w-full text-xl font-bold text-white" />
            </div>
            <div>
              <label className="block text-gray-400 text-xs mb-2">Descripción (opcional)</label>
              <input value={description} onChange={e => setDescription(e.target.value)}
                placeholder="¿Para qué es este pago?" className="input-glass w-full text-sm" />
            </div>
            <p className="text-gray-600 text-xs">Se aplicará una comisión al monto enviado.</p>
            <div className="flex gap-3">
              <button onClick={() => { setStep("recipient"); setRecipient(null); }} className="btn-secondary flex-1">Atrás</button>
              <button onClick={send} disabled={sending || !amount} className="btn-primary flex-1 flex items-center justify-center gap-2">
                {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
                Enviar
              </button>
            </div>
          </div>
        )}

        {step === "done" && result && (
          <div className="p-6 flex flex-col items-center gap-4 text-center">
            <div className="w-16 h-16 rounded-2xl bg-green-500/20 flex items-center justify-center">
              <Check size={32} className="text-green-400" />
            </div>
            <div>
              <p className="text-white font-bold text-xl">${fmt(result.transaction?.amount)}</p>
              <p className="text-gray-400 text-sm mt-1">{result.message}</p>
            </div>
            <div className="w-full glass-panel-sm p-3 text-left space-y-1">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Enviado</span>
                <span className="text-white">${fmt(result.transaction?.amount)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500">Comisión</span>
                <span className="text-yellow-400">${fmt(result.transaction?.fee)}</span>
              </div>
              <div className="flex justify-between text-xs font-semibold">
                <span className="text-gray-400">Recibió</span>
                <span className="text-green-400">${fmt(result.transaction?.net_amount)}</span>
              </div>
            </div>
            <button onClick={onClose} className="btn-primary w-full">Listo</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Recharge Modal ────────────────────────────────────────────────────────────
function RechargeModal({ wallet, onClose, onDone }) {
  const [method, setMethod] = useState(null);
  const [amount, setAmount] = useState("");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);

  const recharge = async () => {
    if (!method) { toast.error("Selecciona un método"); return; }
    if (!amount || parseFloat(amount) <= 0) { toast.error("Ingresa un monto válido"); return; }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/wallet/recharge`, {
        owner_id: wallet.owner_id,
        amount: parseFloat(amount),
        method,
        reference,
      });
      if (r.data.status === "completed") {
        toast.success(`Recarga de $${fmt(parseFloat(amount))} MXN completada`);
      } else {
        toast.success("Recarga enviada. Pendiente de aprobación por administrador.");
      }
      onDone();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="glass-panel w-full max-w-sm">
        <div className="flex items-center justify-between p-6 pb-4 border-b border-white/10">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <ArrowDownLeft size={16} className="text-green-400" />Recargar Wallet
          </h3>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-lg"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-gray-400 text-xs mb-2 font-medium">Método de pago</label>
            <div className="space-y-2">
              {Object.entries(METHOD_INFO).map(([key, info]) => {
                const Icon = info.icon;
                const sel = method === key;
                return (
                  <button key={key} onClick={() => setMethod(key)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${sel ? `${info.bg} ${info.border} ${info.color}` : "bg-white/3 border-white/5 text-gray-400 hover:bg-white/8"}`}>
                    <Icon size={18} />
                    <span className="text-sm font-medium text-left">{info.label}</span>
                    {sel && <Check size={14} className="ml-auto" />}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="block text-gray-400 text-xs mb-1.5 font-medium">Monto ($MXN)</label>
            <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
              placeholder="0.00" className="input-glass w-full text-lg font-bold" />
          </div>
          {(method === "spei" || method === "crypto") && (
            <div>
              <label className="block text-gray-400 text-xs mb-1.5 font-medium">Referencia / Hash de transacción</label>
              <input value={reference} onChange={e => setReference(e.target.value)}
                placeholder={method === "spei" ? "Clave de rastreo SPEI" : "Hash de TX"}
                className="input-glass w-full text-sm" />
            </div>
          )}
          {method && method !== "stripe" && (
            <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-yellow-400 text-xs flex items-start gap-1.5">
                <Clock size={13} className="flex-shrink-0 mt-0.5" />
                Tu recarga quedará pendiente de verificación por el administrador (1–24 horas).
              </p>
            </div>
          )}
          <button onClick={recharge} disabled={loading || !method || !amount} className="btn-primary w-full flex items-center justify-center gap-2">
            {loading ? <RefreshCw size={14} className="animate-spin" /> : <ArrowDownLeft size={14} />}
            Recargar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main OkWallet Module ──────────────────────────────────────────────────────
export default function OkWalletModule({ user }) {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [payLinks, setPayLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [showRecharge, setShowRecharge] = useState(false);
  const [showBalance, setShowBalance] = useState(true);
  const [activeTab, setActiveTab] = useState("home");

  const ownerId = user?.id;

  const loadWallet = useCallback(async () => {
    if (!ownerId) return;
    try {
      const [w, t] = await Promise.all([
        axios.get(`${API}/wallet/me/${ownerId}`),
        axios.get(`${API}/wallet/transactions/${ownerId}?limit=30`),
      ]);
      setWallet(w.data);
      setTransactions(t.data);
      try {
        const pl = await axios.get(`${API}/wallet/payment-links/${ownerId}`);
        setPayLinks(pl.data);
      } catch {}
    } catch (e) {
      if (e.response?.status === 404) setWallet(null);
    }
    finally { setLoading(false); }
  }, [ownerId]);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  const createWallet = async () => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/wallet/create`, {
        owner_id: ownerId,
        owner_type: user?.role === "business" ? "business" : "user",
        username: user?.okuser || user?.okbusiness || user?.name || "user",
      });
      setWallet(r.data);
      toast.success("¡OkWallet creada exitosamente!");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear wallet"); }
    finally { setCreating(false); }
  };

  const handleDisputeTx = async (txId) => {
    const reason = window.prompt("¿Cuál es el problema con esta transacción?");
    if (!reason) return;
    try {
      await axios.post(`${API}/wallet/dispute`, { transaction_id: txId, reporter_id: ownerId, reason });
      toast.success("Disputa enviada al administrador");
      loadWallet();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw size={28} className="text-red-400 animate-spin" />
          <p className="text-gray-500 text-sm">Cargando OkWallet...</p>
        </div>
      </div>
    );
  }

  if (!wallet) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[500px] px-6">
        <div className="relative mb-8">
          <div className="absolute inset-0 rounded-full blur-2xl" style={{ background: "radial-gradient(ellipse, rgba(239,68,68,0.2) 0%, transparent 70%)" }} />
          <div className="relative w-24 h-24 rounded-3xl flex items-center justify-center" style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.2) 0%, rgba(139,92,246,0.1) 100%)", border: "1px solid rgba(239,68,68,0.3)" }}>
            <Wallet size={40} className="text-red-400" />
          </div>
        </div>
        <h2 className="text-white font-bold text-2xl mb-2">Activa tu OkWallet</h2>
        <p className="text-gray-500 text-sm text-center mb-8 max-w-xs">Tu cartera digital personal para enviar y recibir pagos, recargar saldo y generar links de cobro.</p>
        <div className="grid grid-cols-3 gap-4 mb-8 w-full max-w-xs">
          {[
            { icon: Shield, label: "100% Seguro" },
            { icon: Zap, label: "Instantáneo" },
            { icon: Link2, label: "Links de pago" },
          ].map((f, i) => (
            <div key={i} className="flex flex-col items-center gap-1.5 p-3 rounded-2xl" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <f.icon size={20} className="text-red-400" />
              <span className="text-gray-400 text-xs text-center">{f.label}</span>
            </div>
          ))}
        </div>
        <button onClick={createWallet} disabled={creating}
          className="w-full max-w-xs py-4 rounded-2xl text-white font-bold text-base flex items-center justify-center gap-2 transition-all"
          style={{ background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)", boxShadow: "0 0 30px rgba(239,68,68,0.3)" }}>
          {creating ? <RefreshCw size={18} className="animate-spin" /> : <Sparkles size={18} />}
          Crear mi OkWallet
        </button>
      </div>
    );
  }

  const pendingTxs = transactions.filter(t => t.status === "pending");
  const sentTxs = transactions.filter(t => t.sender_id === ownerId && t.type === "transfer");
  const receivedTxs = transactions.filter(t => t.receiver_id === ownerId);

  return (
    <div className="flex flex-col h-full">
      {/* Header / Balance Card — always dark for fintech clarity */}
      <div className="okwallet-balance-card relative overflow-hidden rounded-2xl mx-4 mt-4 mb-4 p-6" style={{ background: "linear-gradient(135deg, #1a0a0a 0%, #1a0d1a 50%, #0d0a1a 100%)", border: "1px solid rgba(239,68,68,0.2)" }}>
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full blur-3xl pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(239,68,68,0.12) 0%, transparent 70%)" }} />
        <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full blur-2xl pointer-events-none" style={{ background: "radial-gradient(ellipse at center, rgba(139,92,246,0.08) 0%, transparent 70%)" }} />
        <div className="relative">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg" style={{ background: "rgba(239,68,68,0.2)" }}>
                <Wallet size={14} style={{ color: "#f87171", margin: "6.5px auto" }} />
              </div>
              <span className="okwallet-subtext text-xs font-medium">OkWallet</span>
            </div>
            <button onClick={() => setShowBalance(b => !b)} className="p-1.5" style={{ color: "rgba(255,255,255,0.5)" }}>
              {showBalance ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
          </div>
          <p className="okwallet-subtext text-xs mb-1">Saldo disponible</p>
          <p className="okwallet-text font-bold mb-3" style={{ fontSize: "2.5rem", lineHeight: 1 }}>
            {showBalance ? `$${fmt(wallet.balance)}` : "$ ••••••"}
            <span className="okwallet-subtext text-sm font-normal ml-2">MXN</span>
          </p>
          <p className="okwallet-id font-mono text-xs mb-4">{wallet.wallet_id}</p>
          <div className="flex gap-2">
            <button onClick={() => setShowRecharge(true)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all" style={{ background: "rgba(34,197,94,0.2)", border: "1px solid rgba(34,197,94,0.35)", color: "#4ade80" }}>
              <ArrowDownLeft size={14} />Recargar
            </button>
            <button onClick={() => setShowTransfer(true)} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all" style={{ background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.35)", color: "#f87171" }}>
              <Send size={14} />Enviar
            </button>
            <button onClick={() => setShowQR(true)} className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all" style={{ background: "rgba(139,92,246,0.2)", border: "1px solid rgba(139,92,246,0.35)", color: "#c4b5fd" }}>
              <QrCode size={14} />QR
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1.5 px-4 mb-4">
        {[
          { id: "home", label: "Resumen" },
          { id: "history", label: "Historial" },
          { id: "links", label: "Links de Pago" },
        ].map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`flex-1 py-2 rounded-xl text-xs font-semibold transition-all ${activeTab === t.id ? "okwallet-tab-active" : "okwallet-tab-inactive"}`}
            style={activeTab === t.id ? { background: "#ef4444", color: "white" } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {activeTab === "home" && (
          <div className="space-y-4">
            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Enviado", value: sentTxs.reduce((a, t) => a + t.amount, 0), color: "#ef4444" },
                { label: "Recibido", value: receivedTxs.reduce((a, t) => a + (t.net_amount||0), 0), color: "#22c55e" },
                { label: "Pendientes", value: pendingTxs.length, color: "#f59e0b", isCount: true },
              ].map((s, i) => (
                <div key={i} className="okwallet-card glass-panel-sm p-3 text-center rounded-xl">
                  <p className="text-lg font-bold" style={{ color: s.color }}>{s.isCount ? s.value : `$${fmt(s.value)}`}</p>
                  <p className="okwallet-section-label text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Recent transactions */}
            {transactions.length > 0 && (
              <div>
                <p className="okwallet-section-label text-xs font-semibold uppercase tracking-wider mb-3">Últimas transacciones</p>
                <div className="space-y-2">
                  {transactions.slice(0, 5).map(tx => {
                    const isOut = tx.sender_id === ownerId && tx.type === "transfer";
                    const icon = isOut ? ArrowUpRight : ArrowDownLeft;
                    const amountColor = isOut ? "#ef4444" : "#22c55e";
                    const sign = isOut ? "-" : "+";
                    const displayAmount = isOut ? tx.amount : tx.net_amount || tx.amount;
                    const Icon = icon;
                    return (
                      <div key={tx.id} className="okwallet-card flex items-center gap-3 p-3 rounded-xl border" style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.07)" }}>
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: isOut ? "rgba(239,68,68,0.12)" : "rgba(34,197,94,0.12)" }}>
                          <Icon size={16} style={{ color: amountColor }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="okwallet-tx-title text-sm font-medium truncate">
                            {tx.type === "recharge" ? `Recarga vía ${tx.method?.toUpperCase()}` : tx.description || "Transferencia"}
                          </p>
                          <p className="okwallet-tx-date text-xs">{fmtDate(tx.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-sm" style={{ color: amountColor }}>{sign}${fmt(displayAmount)}</p>
                          <span className="text-xs px-1.5 py-0.5 rounded-full" style={tx.status === "completed" ? { color: "#22c55e", background: "rgba(34,197,94,0.1)" } : tx.status === "pending" ? { color: "#f59e0b", background: "rgba(245,158,11,0.1)" } : { color: "#f97316", background: "rgba(249,115,22,0.1)" }}>
                            {tx.status === "completed" ? "OK" : tx.status === "pending" ? "Pendiente" : "Disputa"}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {transactions.length === 0 && (
              <div className="glass-panel p-12 text-center rounded-2xl">
                <History size={28} style={{ color: "#9ca3af", margin: "0 auto 12px" }} />
                <p className="okwallet-empty text-sm">Sin movimientos aún</p>
                <p className="okwallet-empty text-xs mt-1">Recarga tu wallet para empezar</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "history" && (
          <div className="space-y-2">
            {transactions.map(tx => {
              const isOut = tx.sender_id === ownerId && tx.type === "transfer";
              const amountColor = isOut ? "#ef4444" : "#22c55e";
              const Icon = isOut ? ArrowUpRight : ArrowDownLeft;
              const sign = isOut ? "-" : "+";
              return (
                <div key={tx.id} className="okwallet-card glass-panel-sm p-4 rounded-xl">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: isOut ? "rgba(239,68,68,0.1)" : "rgba(34,197,94,0.1)" }}>
                      <Icon size={16} style={{ color: amountColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="okwallet-tx-title text-sm font-semibold">{tx.description || "Transacción"}</p>
                          <p className="okwallet-tx-date text-xs mt-0.5">{fmtDate(tx.created_at)}</p>
                          {tx.method && <p className="okwallet-empty text-xs capitalize mt-0.5">{tx.type === "recharge" ? `Recarga · ${tx.method}` : `${tx.sender_wallet || ""} → ${tx.receiver_wallet || ""}`}</p>}
                        </div>
                        <div className="text-right ml-3">
                          <p className="font-bold" style={{ color: amountColor }}>{sign}${fmt(isOut ? tx.amount : (tx.net_amount || tx.amount))}</p>
                          {tx.fee > 0 && <p className="okwallet-empty text-xs">fee ${fmt(tx.fee)}</p>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs px-2 py-0.5 rounded-full" style={tx.status === "completed" ? { color: "#22c55e", background: "rgba(34,197,94,0.1)" } : tx.status === "pending" ? { color: "#f59e0b", background: "rgba(245,158,11,0.1)" } : { color: "#f97316", background: "rgba(249,115,22,0.1)" }}>
                          {tx.status === "completed" ? "Completada" : tx.status === "pending" ? "Pendiente aprobación" : "En disputa"}
                        </span>
                        <button onClick={() => handleDisputeTx(tx.id)} className="text-xs flex items-center gap-1" style={{ color: "#9ca3af" }}>
                          <Flag size={11} />Reportar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {transactions.length === 0 && (
              <div className="glass-panel p-12 text-center rounded-2xl">
                <History size={28} style={{ color: "#9ca3af", margin: "0 auto 12px" }} />
                <p className="okwallet-empty">Sin historial de transacciones</p>
              </div>
            )}
          </div>
        )}

        {activeTab === "links" && (
          <div className="space-y-4">
            <button onClick={() => setShowQR(true)} className="w-full flex items-center justify-center gap-2 py-3 rounded-xl btn-primary">
              <Plus size={16} />Nuevo link de pago
            </button>
            {payLinks.length > 0 ? (
              <div className="space-y-3">
                {payLinks.map(link => (
                  <div key={link.id} className="glass-panel-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-bold">${fmt(link.amount)} MXN</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${link.used ? "bg-gray-500/10 text-gray-400" : "bg-green-500/10 text-green-400"}`}>
                        {link.used ? "Usado" : "Activo"}
                      </span>
                    </div>
                    {link.description && <p className="text-gray-400 text-xs mb-2">{link.description}</p>}
                    <div className="flex items-center gap-2">
                      <code className="text-gray-500 text-xs flex-1 truncate font-mono">{link.qr_data}</code>
                      <button onClick={() => { navigator.clipboard.writeText(link.qr_data); toast.success("Copiado"); }}
                        className="p-1.5 bg-white/5 rounded-lg text-gray-400 hover:text-white">
                        <Copy size={13} />
                      </button>
                    </div>
                    <p className="text-gray-600 text-xs mt-1">Expira: {fmtDate(link.expires_at)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="glass-panel p-12 text-center">
                <Link2 size={28} className="text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400 text-sm">Sin links de pago creados</p>
              </div>
            )}
          </div>
        )}
      </div>

      {showQR && <QRModal wallet={wallet} onClose={() => setShowQR(false)} />}
      {showTransfer && <TransferModal wallet={wallet} onClose={() => setShowTransfer(false)} onDone={loadWallet} />}
      {showRecharge && <RechargeModal wallet={wallet} onClose={() => setShowRecharge(false)} onDone={loadWallet} />}
    </div>
  );
}
